import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { MagicWand } from "@phosphor-icons/react"
import { buttonVariants } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { useToast } from "@/hooks/useToast"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { cn } from "@/lib/utils"
import { useAppendChatHistoryMessage } from "@/modules/chats/hooks/useChatHistory"
import { showAIErrorMessages } from "@/modules/chats/lib/show-ai-error-messages"
import { magicWandQuery } from "@/modules/chats/services/ai/magicWandQuery"
import { useChatInputContext } from "@/modules/chats/stores/useChatInputStore"
import { AiTrackingMetadata } from "@/modules/chats/types/chat-message"
import { ChatThread, getAutoTranslationTargetLanguage } from "@/modules/chats/types/chat-thread"
import { MagicWandType } from "@/types/__generated__/union-types"

export const MagicWandButton = (props: { selectedThread: ChatThread }) => {
  const { selectedThread } = props
  const { t } = useTranslation()
  const { toast } = useToast()

  const isAiRunning = useChatInputContext((store) => store.aiRunning !== "NONE")
  const isSendingMessage = useChatInputContext((store) => store.isSendingMessage)
  const messageText = useChatInputContext((store) => store.messageText)
  const setAiRunning = useChatInputContext((store) => store.setAiRunning)
  const setAiTrackingMetadata = useChatInputContext((store) => store.setAiTrackingMetadata)
  const appendHistory = useAppendChatHistoryMessage()
  const targetAgentLanguage = getAutoTranslationTargetLanguage(selectedThread)

  const handleMagicWand = useCallback(
    async (magicWandType: MagicWandType) => {
      try {
        setAiRunning("MAGIC_WAND")
        sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))
        const aiFeatureId = uuidv4()
        const result = await magicWandQuery(selectedThread.patient.country, {
          magicWandType: magicWandType,
          aiRequestMetadata: {
            aiFeatureId,
            appointmentId: selectedThread.appointmentId,
            age: selectedThread.patient.age.years,
            gender: selectedThread.patient.gender,
            userInput: messageText,
            targetAgentLanguage,
          },
        })

        if (!result) {
          return
        }

        if (result.magicWand.result === "SUCCESS") {
          appendHistory(result.magicWand.text, `MAGIC_WAND_${magicWandType}`, messageText)

          FaroInsightsWrapper.pushEvent("aiFeature_magicWand", {
            aiFeatureId,
            feature: "Magic Wand " + magicWandType,
            fileHash: result.magicWand.fileHash,
            aiRequestDurationMs: result.magicWand.aiRequestDurationMs.toString(),
            aiAnonymizationRequestDurationMs: result.magicWand.aiAnonymizationRequestDurationMs.toString(),
            sentAt: new Date().toISOString(),
          })

          const trackingMetadata: AiTrackingMetadata = {
            id: aiFeatureId,
            name: "Magic Wand",
            previousText: messageText,
            newText: result.magicWand.text,
            aiFeature: `MAGIC_WAND_${magicWandType}`,
            fileHash: result.magicWand.fileHash,
            aiRequestDurationMs: result.magicWand.aiRequestDurationMs,
            aiAnonymizationRequestDurationMs: result.magicWand.aiAnonymizationRequestDurationMs,
            createdAt: new Date().toISOString(),
          }
          setAiTrackingMetadata([trackingMetadata])
        } else {
          showAIErrorMessages(result.magicWand.result)
        }
      } catch {
        toast({
          description: t("chat.ai.errors.UNKNOWN_ERROR"),
        })
      } finally {
        setAiRunning("NONE")
        sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
      }
    },
    [messageText, selectedThread, targetAgentLanguage, t, toast, setAiRunning, setAiTrackingMetadata, appendHistory],
  )

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild disabled={isAiRunning || isSendingMessage || messageText.trim().length < 2}>
        <button
          className={cn(
            buttonVariants({ variant: "circle" }),
            "h-8 w-8 disabled:cursor-not-allowed disabled:bg-transparent",
          )}
          data-testid="magic-wand-button">
          <MagicWand size={24} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-50 rounded-lg p-2" onMouseDown={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          className="focus:bg-surface-hovered rounded-md py-2"
          onClick={() => handleMagicWand("EXPLAIN")}>
          {t("chat.magicWand.explain")}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="focus:bg-surface-hovered rounded-md py-2"
          onClick={() => handleMagicWand("THERAPY")}>
          {t("chat.magicWand.therapy")}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="focus:bg-surface-hovered rounded-md py-2"
          onClick={() => handleMagicWand("ANAMNESE")}>
          {t("chat.magicWand.anamnese")}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="focus:bg-surface-hovered rounded-md py-2"
          onClick={() => handleMagicWand("NICE_SPEAK")}>
          {t("chat.magicWand.niceSpeak")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
